import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import Typography from "@/components/ui/typography";
import {
  UpdateFounderSettingsPayload,
  updateOwnFounderSettings,
} from "@/features/founders/api/founders";
import { useToast } from "@/hooks/use-toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { BusinessCategory } from "@/types/user";

const profileDetailsSchema = z.object({
  niche: z
    .string()
    .max(100, "Niche must be less than 100 characters")
    .optional(),
  nicheWho: z.string().optional(),
  nicheWhat: z.string().optional(),
  businessCategory: z.array(z.nativeEnum(BusinessCategory)).optional(),
});

type ProfileDetailsFormValues = z.infer<typeof profileDetailsSchema>;

const ProfileDetailsSection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const hasSetInitialValues = React.useRef(false);

  const form = useForm<ProfileDetailsFormValues>({
    resolver: zodResolver(profileDetailsSchema),
    defaultValues: {
      niche: "",
      nicheWho: "",
      nicheWhat: "",
      businessCategory: [],
    },
  });

  // Update form values when user data is loaded
  React.useEffect(() => {
    if (user && !hasSetInitialValues.current) {
      // Convert lowercase businessCategory values from backend to our enum values
      const businessCategory =
        user.businessCategory?.map((cat: string) => {
          const normalized =
            cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
          return normalized as BusinessCategory;
        }) || [];

      form.reset({
        niche: user.niche || "",
        nicheWho: user.nicheWho || "",
        nicheWhat: user.nicheWhat || "",
        businessCategory,
      });
      hasSetInitialValues.current = true;
    }
  }, [user, form]);

  const { mutate: updateSettings, isPending } = useMutation({
    mutationFn: updateOwnFounderSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["me"],
      });
      toast({
        title: "Profile updated successfully!",
        description: "Your profile settings have been saved.",
      });
      form.reset(form.getValues());
    },
    onError: () => {
      toast({
        title: "Failed to update profile",
        description: "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ProfileDetailsFormValues) => {
    const payload: UpdateFounderSettingsPayload = {};

    if (data.niche !== undefined) {
      payload.niche = data.niche;
    }

    if (data.nicheWho !== undefined) {
      payload.nicheWho = data.nicheWho;
    }

    if (data.nicheWhat !== undefined) {
      payload.nicheWhat = data.nicheWhat;
    }

    if (data.businessCategory !== undefined) {
      payload.businessCategory = data.businessCategory;
    }

    // Combine nicheWho and nicheWhat into niche for backward compatibility
    if (data.nicheWho && data.nicheWhat) {
      payload.niche = `${data.nicheWho} - ${data.nicheWhat}`;
    }

    updateSettings(payload);
  };

  return (
    <div className="bg-[#f9f9f9] rounded-2xl p-6 space-y-6">
      <div className="space-y-2">
        <Typography size="l2" className="font-semibold text-[#494949]">
          Niche and expertise
        </Typography>
        <Typography size="b2" className="text-[#767677]">
          Define your target market and areas of expertise. This will be used to
          match you with mentors or relevant community members.
        </Typography>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="businessCategory"
            render={() => (
              <FormItem>
                <Typography size="l2" className="font-semibold text-[#494949]">
                  Consulting category
                </Typography>
                <div className="flex flex-wrap gap-3 items-center mt-2">
                  {Object.values(BusinessCategory).map((category) => (
                    <FormField
                      key={category}
                      control={form.control}
                      name="businessCategory"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={category}
                            className="flex flex-row items-center gap-2"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(category)}
                                onCheckedChange={(checked) => {
                                  const currentValue = field.value || [];
                                  return checked
                                    ? field.onChange([
                                        ...currentValue,
                                        category,
                                      ])
                                    : field.onChange(
                                        currentValue.filter(
                                          (value) => value !== category,
                                        ),
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-medium text-[#767677] !mt-0">
                              {category}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="nicheWho"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Who You Serve</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="E.g. Real estate agents, Medical practitioners"
                      className="bg-[#fdfdfd]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nicheWhat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What You Offer</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="E.g. Lead generation, Educational workshops"
                      className="bg-[#fdfdfd]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button
            type="submit"
            disabled={isPending || !form.formState.isDirty}
            className="text-white rounded-lg h-9 px-5"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ProfileDetailsSection;
